import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import api, { getApiError } from '../../../services/api';

export interface Resource<T> {
    data: T | null;
    setData: Dispatch<SetStateAction<T | null>>;
    /** idle = no url yet. */
    status: 'idle' | 'loading' | 'error' | 'ready';
    /** The last request answered 404 (status is 'error'). */
    missing: boolean;
    reload: () => void;
}

/** GET `url` whenever it changes. A null url stays idle. */
export function useResource<T>(url: string | null): Resource<T> {
    const [data, setData] = useState<T | null>(null);
    const [status, setStatus] = useState<Resource<T>['status']>(url ? 'loading' : 'idle');
    const [missing, setMissing] = useState(false);
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        if (!url) {
            setStatus('idle');
            return;
        }
        let stale = false;
        setStatus('loading');
        api.get<T>(url)
            .then((res) => {
                if (stale) return;
                setData(res.data);
                setMissing(false);
                setStatus('ready');
            })
            .catch((e: unknown) => {
                if (stale) return;
                setMissing(getApiError(e).status === 404);
                setStatus('error');
            });
        return () => {
            stale = true;
        };
    }, [url, nonce]);

    const reload = useCallback(() => setNonce((n) => n + 1), []);
    return { data, setData, status, missing, reload };
}
